import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, Phone, Mail, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { APPLIANCES } from "@/lib/appliances";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const inputClass =
  "w-full h-14 bg-[#0C1C2E] border border-[#223851] rounded-xl px-4 text-white placeholder:text-[#6B7E92] focus:border-[#F26A21] focus:outline-none focus:ring-2 focus:ring-[#F26A21]/30 transition-all duration-300";

const SERVICE_LABELS = {
  "repair-only": "Any Product Repair (₹350)",
  "product-installation": "Any Product Installation (₹399)",
  "ac-installation": "AC Installation (₹799)",
  "ac-removal": "AC Removal (₹399)",
};

export default function Enquire() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    appliance: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const svc = params.get("service");
    if (svc && SERVICE_LABELS[svc]) {
      setForm((f) => ({
        ...f,
        message: `I'd like to book: ${SERVICE_LABELS[svc]}.`,
      }));
    }
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.appliance) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry received. Our team will call you within 24 hours.");
      setForm({ name: "", email: "", phone: "", appliance: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="enquire-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(242,106,33,0.10),transparent_55%)]" />
        <div className="relative max-w-[1300px] mx-auto px-5 sm:px-8 pt-14 md:pt-20 pb-6">
          <motion.p className="overline text-[#F26A21] mb-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            Tell us what needs care
          </motion.p>
          <motion.h1
            className="font-heading font-bold text-5xl sm:text-6xl tracking-tight text-white leading-[1.05]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Let's get you{" "}
            <span className="text-[#F26A21]">sorted.</span>
          </motion.h1>
        </div>
      </section>

      <section className="max-w-[1300px] mx-auto px-5 sm:px-8 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading font-bold text-2xl text-white leading-tight">
              Not sure which plan fits?
            </h2>
            <p className="text-[#AEBECD] mt-3 leading-relaxed">
              Share a few details and an ASA advisor will recommend the right
              plan for your home — with a free, no-obligation quote.
            </p>
            <div className="mt-8 space-y-3">
              {[
                [Phone, "Call us", "+91-9059223423"],
                [Phone, "Or", "+91-7386685959"],
                [Mail, "Email", "asahomeappliancesservices@gmail.com"],
                [Clock, "Hours", "24/7 emergency hotline"],
              ].map(([Icon, l, v], i) => (
                <div key={i} className="flex items-center gap-4 rounded-2xl bg-[#122539] border border-[#223851] p-4">
                  <span className="w-10 h-10 rounded-xl bg-[#F26A21]/15 flex items-center justify-center shrink-0">
                    <Icon size={18} className="text-[#F26A21]" />
                  </span>
                  <div className="min-w-0">
                    <p className="overline text-[#6B7E92]">{l}</p>
                    <p className="text-white truncate">{v}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            data-testid="enquiry-form"
            className="lg:col-span-7 rounded-3xl bg-[#122539] border border-[#223851] p-6 sm:p-8 space-y-5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="overline text-[#8CA0B4] block mb-2">Full name *</label>
                <input data-testid="enquiry-name" className={inputClass} value={form.name} onChange={set("name")} placeholder="Your name" />
              </div>
              <div>
                <label className="overline text-[#8CA0B4] block mb-2">Phone *</label>
                <input data-testid="enquiry-phone" className={inputClass} value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
              </div>
            </div>

            <div>
              <label className="overline text-[#8CA0B4] block mb-2">Email *</label>
              <input data-testid="enquiry-email" type="email" className={inputClass} value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </div>

            <div>
              <label className="overline text-[#8CA0B4] block mb-2">Appliance *</label>
              <Select value={form.appliance} onValueChange={(v) => setForm({ ...form, appliance: v })}>
                <SelectTrigger
                  data-testid="enquiry-appliance"
                  className="w-full h-14 bg-[#0C1C2E] border border-[#223851] rounded-xl px-4 text-white focus:border-[#F26A21] data-[placeholder]:text-[#6B7E92]"
                >
                  <SelectValue placeholder="Select appliance" />
                </SelectTrigger>
                <SelectContent className="bg-[#122539] border-[#223851] text-white">
                  {[...APPLIANCES, "Multiple appliances"].map((a) => (
                    <SelectItem key={a} value={a} data-testid={`appliance-opt-${a}`}>
                      {a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="overline text-[#8CA0B4] block mb-2">Message</label>
              <textarea
                data-testid="enquiry-message"
                className="w-full bg-[#0C1C2E] border border-[#223851] rounded-xl px-4 py-3 text-white placeholder:text-[#6B7E92] focus:border-[#F26A21] focus:outline-none focus:ring-2 focus:ring-[#F26A21]/30 transition-all duration-300 min-h-[130px] resize-none"
                value={form.message}
                onChange={set("message")}
                placeholder="Describe the issue or your requirements..."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="enquiry-submit"
              className="w-full h-14 bg-[#F26A21] text-white rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-[#DE5C15] transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <>Send enquiry <Send size={16} /></>}
            </button>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
